// src/pages/AsignarInventarioPage.tsx
import { useState, useEffect } from 'react'
import api from '../services/api'
import { Package, Plus, Search, ArrowRight, Undo2 } from 'lucide-react'

interface Distribuidor {
  id: number
  nombre: string
  email: string
}

interface Producto {
  id_producto: number
  detalle: string
  codigo_barras: string
  cantidad: number
  precio_venta_a: number
}

interface InventarioDistribuidor {
  id: number
  id_producto: number
  producto_nombre: string
  codigo_barras: string
  cantidad_asignada: number
  cantidad_disponible: number
  precio_venta_a: number
}

interface ResumenDistribuidor {
  id_usuario: number
  nombre: string
  email: string
  productos_asignados: number
  total_asignado: number
  total_disponible: number
  total_vendido: number
}

export default function AsignarInventarioPage() {
  const [distribuidores, setDistribuidores] = useState<Distribuidor[]>([])
  const [productos, setProductos] = useState<Producto[]>([])
  const [resumen, setResumen] = useState<ResumenDistribuidor[]>([])
  const [inventarioSeleccionado, setInventarioSeleccionado] = useState<InventarioDistribuidor[]>([])
  const [distribuidorSeleccionado, setDistribuidorSeleccionado] = useState<Distribuidor | null>(null)
  const [loading, setLoading] = useState(true)
  const [searchProducto, setSearchProducto] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [asignacion, setAsignacion] = useState({
    id_usuario: 0,
    id_producto: 0,
    cantidad: 1
  })

  useEffect(() => {
    cargarDatos()
  }, [])

  const cargarDatos = async () => {
    try {
      const [distRes, resumenRes] = await Promise.all([
        api.get('/usuarios/distribuidores'),
        api.get('/inventario/resumen-distribuidores')
      ])
      setDistribuidores(distRes.data.data)
      setResumen(resumenRes.data.data)
    } catch (error) {
      console.error('Error cargando datos:', error)
    } finally {
      setLoading(false)
    }
  }

  const buscarProductos = async (search: string) => {
    setSearchProducto(search)
    if (search.length < 2) {
      setProductos([])
      return
    }
    try {
      const { data } = await api.get(`/productos?search=${search}`)
      setProductos(data.data)
    } catch (error) {
      console.error('Error buscando productos:', error)
    }
  }

  const verInventarioDistribuidor = async (distribuidor: Distribuidor) => {
    setDistribuidorSeleccionado(distribuidor)
    try {
      const { data } = await api.get(`/inventario/distribuidor/${distribuidor.id}`)
      setInventarioSeleccionado(data.inventario)
    } catch (error) {
      console.error('Error cargando inventario:', error)
    }
  }

  const handleAsignar = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await api.post('/inventario/asignar', asignacion)
      alert('Inventario asignado correctamente')
      setShowModal(false)
      setAsignacion({ id_usuario: 0, id_producto: 0, cantidad: 1 })
      cargarDatos()
      if (distribuidorSeleccionado) {
        verInventarioDistribuidor(distribuidorSeleccionado)
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error al asignar')
    }
  }

  const handleDevolver = async (id_usuario: number, id_producto: number, cantidadDisponible: number) => {
    const cantidad = prompt(`Cantidad a devolver (máx: ${cantidadDisponible}):`, '1')
    if (!cantidad) return
    
    const cantidadNum = parseInt(cantidad)
    if (isNaN(cantidadNum) || cantidadNum <= 0 || cantidadNum > cantidadDisponible) {
      alert('Cantidad inválida')
      return
    }

    try {
      await api.post('/inventario/devolver', { id_usuario, id_producto, cantidad: cantidadNum })
      alert('Inventario devuelto correctamente')
      cargarDatos()
      if (distribuidorSeleccionado) {
        verInventarioDistribuidor(distribuidorSeleccionado)
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error al devolver')
    }
  }

  const abrirModalAsignar = (distribuidor: Distribuidor) => {
    setAsignacion({ ...asignacion, id_usuario: distribuidor.id })
    setShowModal(true)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Package className="w-6 h-6" />
          Asignar Inventario a Distribuidores
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lista de Distribuidores */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h2 className="font-semibold">Distribuidores</h2>
          </div>
          <div className="divide-y max-h-[600px] overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">Cargando...</div>
            ) : resumen.length === 0 ? (
              <div className="p-4 text-center text-gray-500">No hay distribuidores</div>
            ) : (
              resumen.map((dist) => (
                <div
                  key={dist.id_usuario}
                  className={`p-4 hover:bg-gray-50 cursor-pointer ${
                    distribuidorSeleccionado?.id === dist.id_usuario ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => verInventarioDistribuidor({ id: dist.id_usuario, nombre: dist.nombre, email: dist.email })}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{dist.nombre}</p>
                      <p className="text-sm text-gray-500">{dist.email}</p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        abrirModalAsignar({ id: dist.id_usuario, nombre: dist.nombre, email: dist.email })
                      }}
                      className="p-2 bg-blue-100 text-blue-600 rounded hover:bg-blue-200"
                      title="Asignar producto"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                    <div className="bg-gray-100 p-2 rounded text-center">
                      <p className="text-gray-500">Productos</p>
                      <p className="font-semibold">{dist.productos_asignados}</p>
                    </div>
                    <div className="bg-green-100 p-2 rounded text-center">
                      <p className="text-gray-500">Disponible</p>
                      <p className="font-semibold text-green-700">{dist.total_disponible}</p>
                    </div>
                    <div className="bg-blue-100 p-2 rounded text-center">
                      <p className="text-gray-500">Vendido</p>
                      <p className="font-semibold text-blue-700">{dist.total_vendido}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Inventario del Distribuidor Seleccionado */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h2 className="font-semibold">
              {distribuidorSeleccionado
                ? `Inventario de ${distribuidorSeleccionado.nombre}`
                : 'Selecciona un distribuidor'}
            </h2>
          </div>
          <div className="divide-y max-h-[600px] overflow-y-auto">
            {!distribuidorSeleccionado ? (
              <div className="p-8 text-center text-gray-400">
                <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Selecciona un distribuidor para ver su inventario</p>
              </div>
            ) : inventarioSeleccionado.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Este distribuidor no tiene productos asignados</p>
              </div>
            ) : (
              inventarioSeleccionado.map((item) => (
                <div key={item.id} className="p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{item.producto_nombre}</p>
                      <p className="text-sm text-gray-500">{item.codigo_barras}</p>
                    </div>
                    <button
                      onClick={() => handleDevolver(distribuidorSeleccionado.id, item.id_producto, item.cantidad_disponible)}
                      className="p-2 text-orange-600 hover:bg-orange-50 rounded"
                      title="Devolver"
                      disabled={item.cantidad_disponible === 0}
                    >
                      <Undo2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="mt-2 flex gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Asignado:</span>{' '}
                      <span className="font-medium">{item.cantidad_asignada}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Disponible:</span>{' '}
                      <span className={`font-medium ${item.cantidad_disponible <= 5 ? 'text-red-600' : 'text-green-600'}`}>
                        {item.cantidad_disponible}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Vendido:</span>{' '}
                      <span className="font-medium text-blue-600">
                        {item.cantidad_asignada - item.cantidad_disponible}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Modal Asignar */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b">
              <h2 className="text-lg font-semibold">Asignar Producto</h2>
              {distribuidores.find(d => d.id === asignacion.id_usuario) && (
                <p className="text-sm text-gray-600 mt-1">
                  Distribuidor: <span className="font-medium">{distribuidores.find(d => d.id === asignacion.id_usuario)?.nombre}</span>
                </p>
              )}
            </div>
            <form onSubmit={handleAsignar} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Buscar Producto
                </label>
                <div className="relative">
                  <Search className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Nombre o código..."
                    value={searchProducto}
                    onChange={(e) => buscarProductos(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg"
                  />
                </div>
                {productos.length > 0 && (
                  <div className="mt-2 border rounded-lg max-h-40 overflow-y-auto">
                    {productos.map((prod) => (
                      <div
                        key={prod.id_producto}
                        className={`p-2 hover:bg-gray-50 cursor-pointer ${
                          asignacion.id_producto === prod.id_producto ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => {
                          setAsignacion({ ...asignacion, id_producto: prod.id_producto })
                          setSearchProducto(prod.detalle)
                          setProductos([])
                        }}
                      >
                        <p className="font-medium text-sm">{prod.detalle}</p>
                        <p className="text-xs text-gray-500">
                          Stock: {prod.cantidad} | ${prod.precio_venta_a}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cantidad a Asignar
                </label>
                <input
                  type="number"
                  min="1"
                  required
                  value={asignacion.cantidad}
                  onChange={(e) => setAsignacion({ ...asignacion, cantidad: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false)
                    setSearchProducto('')
                    setProductos([])
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={!asignacion.id_producto || asignacion.cantidad <= 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <ArrowRight className="w-4 h-4" />
                  Asignar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
